import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { recoverRequestsApi, type VersionUrl } from '../api'
import { Copy, Check, RefreshCw, ChevronDown, ChevronRight, FileKey, Download, FolderDown, Hourglass, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { useNow } from '@/hooks/useNow'
import { tierLabel } from '../lib/tier-label'
import { formatSecondsToDHM } from '@/features/objects/utils/formatTime'
import { useCan } from '@/hooks/useCan'
import {
    chainSections,
    presignedUrlsState,
    presignedPollInterval,
    linksExpiry,
    EXPIRY_WARNING_MINUTES,
    PRESIGNED_POLL_MS,
    type ChainVersionUrls,
    type LinksExpiry,
} from '../lib/presigned-url-state'

interface VersionUrlsTableProps {
    requestID: number | string
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    const kb = bytes / 1024
    if (kb < 1024) return `${kb.toFixed(1)} KB`
    const mb = kb / 1024
    if (mb < 1024) return `${mb.toFixed(1)} MB`
    return `${(mb / 1024).toFixed(2)} GB`
}

function formatExpiry(iso: string | null, now: number): { text: string; isExpired: boolean } {
    if (!iso) return { text: '—', isExpired: false }
    const d = new Date(iso)
    if (isNaN(d.getTime())) return { text: '—', isExpired: false }
    const isExpired = d.getTime() <= now
    const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    const timeStr = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })
    return { text: `${dateStr} ${timeStr}`, isExpired }
}

function downloadFile(url: string, fileName: string, dirName: string) {
    // Use direct navigation — S3 pre-signed URLs don't support CORS fetch
    const a = document.createElement('a')
    a.href = url
    a.download = `${dirName}_${fileName}`
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
}

const isLive = (u: VersionUrl, now: number) =>
    !!u.preSignedUrl && !!u.fileName && (!u.expiryDateTime || new Date(u.expiryDateTime).getTime() > now)

/**
 * The pre-signed URLs for a recovery — one section per version in the object's RECOVERY CHAIN,
 * in chain order: a server's boot then its full, a database's full then its incrementals and
 * logs, a device's full. Every version is copied to egress and needs its own URLs; the card used
 * to show the first version only, so a server's full and a database's logs had no links anywhere
 * (Andrew, 2026-09-21).
 *
 * Each section keeps its own state (lib/presigned-url-state): generating, still copying to
 * egress (released automatically when it lands — no refresh needed), no files, nothing
 * requested, or the links themselves with expired ones marked. Refresh raises the whole chain.
 *
 * While any version is generating or copying, the card re-reads itself every 10 seconds and
 * stops once nothing is on its way. "Check now" re-reads immediately; it reads only and raises
 * nothing. The header counts down to the soonest expiry among the links on show and then says
 * Expired, on a clock that ticks while the page sits open.
 */
export function VersionUrlsTable({ requestID }: VersionUrlsTableProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const [copiedId, setCopiedId] = useState<number | null>(null)
    const [expanded, setExpanded] = useState(false)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const now = useNow(15_000)

    const { data: sections = [], refetch, isFetching } = useQuery({
        queryKey: ['version-urls', requestID],
        queryFn: async () => {
            const res = await recoverRequestsApi.getVersionUrls(requestID)
            return chainSections<VersionUrl>(res.data?.data ?? res.data)
        },
        enabled: !!requestID,
        // Re-read only while something is on its way; false (stop) once every version is settled.
        // Hidden tabs do not poll (refetchIntervalInBackground defaults to false).
        refetchInterval: (query) => presignedPollInterval(query.state.data),
    })

    const withState = sections.map((s) => ({
        ...s,
        state: presignedUrlsState({
            requested: s.requested,
            urlCount: s.files.length,
            noFiles: s.noFiles,
            waitingForEgress: s.waitingForEgress,
        }),
    }))
    const generating = withState.some((s) => s.state === 'generating')
    const waiting = withState.some((s) => s.state === 'waitingForEgress')
    const totalFiles = withState.reduce((n, s) => n + (s.state === 'links' ? s.files.length : 0), 0)
    const linkSections = withState.filter((s) => s.state === 'links')
    const cardExpiry = linksExpiry(linkSections.flatMap((s) => s.files), now)

    // "Expires in 42m", amber inside the last ten minutes, then the word Expired.
    const renderExpiry = (expiry: LinksExpiry) => {
        if (expiry.kind === 'none') return null
        if (expiry.kind === 'expired') {
            return (
                <Badge variant="destructive" className="shrink-0 px-1.5 py-0 text-xs font-normal">
                    {t('recoverRequests.preSignedUrlsExpiredShort', 'Expired')}
                </Badge>
            )
        }
        const soon = expiry.minutesLeft <= EXPIRY_WARNING_MINUTES
        return (
            <span className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-xs ${soon ? 'font-medium text-amber-600' : 'text-muted-foreground'}`}>
                <Clock className="h-3 w-3" aria-hidden="true" />
                {t('recoverRequests.preSignedUrlsExpiresIn', 'Expires in {{time}}', { time: formatSecondsToDHM(expiry.minutesLeft * 60) })}
            </span>
        )
    }

    // Beside a wait: says the card is already re-reading itself, and reads at once. Reads only —
    // raising new requests is the header's refresh, which would replace links that still work.
    const renderAutoCheck = () => (
        <span className="ml-auto flex shrink-0 items-center gap-2 text-muted-foreground">
            <span className="whitespace-nowrap">
                {t('recoverRequests.preSignedUrlsAutoCheck', 'Checking every {{seconds}} seconds', { seconds: PRESIGNED_POLL_MS / 1000 })}
            </span>
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => refetch()} disabled={isFetching}>
                {t('recoverRequests.preSignedUrlsCheckNow', 'Check now')}
            </Button>
        </span>
    )

    const handleCopy = async (url: string, id: number) => {
        try {
            await navigator.clipboard.writeText(url)
            setCopiedId(id)
            toast({ title: 'Copied', description: 'URL copied to clipboard' })
            setTimeout(() => setCopiedId(null), 2000)
        } catch {
            toast({ title: 'Error', description: 'Failed to copy', variant: 'destructive' })
        }
    }

    // A failed request says so. This used to swallow the error and toast "Requested" regardless,
    // so a refresh that raised nothing looked exactly like one that worked.
    // Asking for (fresh) URLs releases recovery data: recovery.data, which the server also checks
    // against the assignment. Copying and downloading a link already issued is for everyone here.
    const canRequest = useCan('recovery.data')
    const handleRefresh = async (e: React.MouseEvent) => {
        e.stopPropagation()
        setIsRefreshing(true)
        let failed = false
        try {
            await recoverRequestsApi.requestPreSignedUrls(requestID)
        } catch {
            failed = true
        }
        await queryClient.refetchQueries({ queryKey: ['version-urls', requestID] })
        setIsRefreshing(false)
        if (failed) {
            toast({ title: t('common.error', 'Error'), description: t('recoverRequests.preSignedUrlsRequestFailed', 'Pre-signed URLs could not be requested. Try again, or check the recovery.'), variant: 'destructive' })
            return
        }
        toast({ title: t('recoverRequests.preSignedUrlsRequestedShort', 'Requested'), description: t('recoverRequests.preSignedUrlsRefreshing', 'Fresh pre-signed URLs requested — they will appear here once regenerated.') })
    }

    // Every live link across the chain, each version into its own folder name.
    const handleDownloadAll = (e: React.MouseEvent) => {
        e.stopPropagation()
        const now = Date.now()
        const jobs = withState
            .filter((s) => s.state === 'links')
            .flatMap((s) => s.files.filter((u) => isLive(u, now)).map((u) => ({ u, dir: s.objectName || 'recovery-files' })))
        if (jobs.length === 0) {
            toast({ title: t('recoverRequests.preSignedUrlsExpiredTitle', 'Links expired'), description: t('recoverRequests.preSignedUrlsExpired', 'These pre-signed URLs have expired — refresh to regenerate them.'), variant: 'destructive' })
            return
        }
        // A small delay between each, so the browser does not block the burst.
        jobs.forEach(({ u, dir }, i) => {
            setTimeout(() => downloadFile(u.preSignedUrl!, u.fileName!, dir), i * 500)
        })
        toast({ title: 'Downloading', description: `${jobs.length} file${jobs.length !== 1 ? 's' : ''}` })
    }

    const renderSection = (s: ChainVersionUrls<VersionUrl> & { state: ReturnType<typeof presignedUrlsState> }) => (
        <div key={`${s.recoverRequestId ?? 'v'}-${s.objectName}`} className="border-t" data-tier={s.tier ?? ''}>
            <div className="flex items-center gap-2 bg-muted/30 px-4 py-2">
                <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-[11px] font-semibold">
                    {tierLabel(t, s.tier)}
                </Badge>
                <span className="min-w-0 truncate font-mono text-[11px] text-muted-foreground" title={s.objectName}>
                    {s.objectName}
                </span>
                {/* One chain, several expiries: each version's links were generated when it landed. */}
                {linkSections.length > 1 && s.state === 'links' && (
                    <span className="ml-auto flex shrink-0">{renderExpiry(linksExpiry(s.files, now))}</span>
                )}
            </div>

            {/* A generation really is under way: hide the links that are about to be replaced. */}
            {s.state === 'generating' && (
                <div className="flex flex-wrap items-center gap-2.5 px-4 py-3 text-xs text-amber-600">
                    <RefreshCw className="h-4 w-4 flex-shrink-0 animate-spin" />
                    <span className="min-w-0">{t('recoverRequests.preSignedUrlsInProgress', 'Generating pre-signed URLs — they will appear here once ready.')}</span>
                    {renderAutoCheck()}
                </div>
            )}

            {/* Still copying: the request is parked and released by the database the moment this
                version lands, so there is nothing to press. */}
            {s.state === 'waitingForEgress' && (
                <div className="flex flex-wrap items-center gap-2.5 px-4 py-3 text-xs text-amber-600">
                    <Hourglass className="h-4 w-4 flex-shrink-0" />
                    <span className="min-w-0">{t('recoverRequests.preSignedUrlsWaitingForEgress', 'Still copying to egress — URLs will be generated when it lands.')}</span>
                    {renderAutoCheck()}
                </div>
            )}

            {/* Nothing in flight and nothing stored: say what to press. */}
            {s.state === 'none' && (
                <div className="px-4 py-3 text-xs text-muted-foreground">
                    {t('recoverRequests.preSignedUrlsNotRequested', 'No pre-signed URLs have been requested for this object — press refresh to generate them.')}
                </div>
            )}

            {/* In egress and empty: there is nothing to sign, so do not suggest asking again. */}
            {s.state === 'noFiles' && (
                <div className="px-4 py-3 text-xs text-muted-foreground">
                    {t('recoverRequests.preSignedUrlsNoFiles', 'The vault reported no files for this version, so there is nothing to generate.')}
                </div>
            )}

            {/* The links, expired ones marked as expired — which is what tells the user to refresh. */}
            {s.state === 'links' && (
                <div className="max-h-[240px] overflow-y-auto">
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm">
                            <tr className="border-b">
                                <th className="px-4 py-2 text-left font-medium">{t('recoverRequests.fileName')}</th>
                                <th className="w-20 whitespace-nowrap px-3 py-2 text-right font-medium">{t('recoverRequests.fileSize', 'Size')}</th>
                                <th className="w-10 px-2 py-2 text-center font-medium"></th>
                                <th className="w-10 px-2 py-2 text-center font-medium"></th>
                                <th className="w-36 whitespace-nowrap px-3 py-2 text-left font-medium">{t('recoverRequests.expiry')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {s.files.map((url: VersionUrl) => {
                                const expiry = formatExpiry(url.expiryDateTime, now)
                                return (
                                    <tr key={url.id} className="border-b transition-colors last:border-b-0 hover:bg-muted/30">
                                        <td className="px-4 py-2.5">
                                            <span className="font-medium text-foreground" title={url.fileName ?? ''}>
                                                {url.fileName || '—'}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2.5 text-right text-muted-foreground">
                                            {url.fileSize != null ? formatFileSize(url.fileSize) : '—'}
                                        </td>
                                        <td className="px-2 py-2.5 text-center">
                                            {url.preSignedUrl && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    disabled={expiry.isExpired}
                                                    onClick={expiry.isExpired ? undefined : () => handleCopy(url.preSignedUrl!, url.id)}
                                                    title={expiry.isExpired ? t('recoverRequests.preSignedUrlExpiredHint', 'Link expired — refresh to regenerate') : 'Copy URL'}
                                                >
                                                    {copiedId === url.id ? (
                                                        <Check className="h-3.5 w-3.5 text-emerald-500" />
                                                    ) : (
                                                        <Copy className="h-3.5 w-3.5" />
                                                    )}
                                                </Button>
                                            )}
                                        </td>
                                        <td className="px-2 py-2.5 text-center">
                                            {url.preSignedUrl && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    disabled={expiry.isExpired}
                                                    onClick={expiry.isExpired ? undefined : () => downloadFile(url.preSignedUrl!, url.fileName!, s.objectName || 'recovery-files')}
                                                    title={expiry.isExpired ? t('recoverRequests.preSignedUrlExpiredHint', 'Link expired — refresh to regenerate') : 'Download file'}
                                                >
                                                    <Download className="h-3.5 w-3.5" />
                                                </Button>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {expiry.isExpired ? (
                                                <Badge variant="destructive" className="px-1.5 py-0 text-xs font-normal">
                                                    {expiry.text}
                                                </Badge>
                                            ) : (
                                                <span className="whitespace-nowrap text-muted-foreground">{expiry.text}</span>
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )

    return (
        <div className="rounded-lg border bg-card shadow-sm">
            {/* Header — always visible; its badge summarises the whole chain. The toggle and the
                actions are siblings: a button inside a button is invalid, and keyboard and
                screen-reader users could not reach the actions separately. */}
            <div className="flex w-full items-center gap-2 rounded-lg px-4 py-3 transition-colors hover:bg-muted/50">
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                >
                    {expanded ? (
                        <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    ) : (
                        <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    )}
                    <FileKey className="h-4 w-4 flex-shrink-0 text-blue-500" />
                    <span className="text-sm font-semibold">{t('recoverRequests.preSignedUrls')}</span>
                    {generating ? (
                        <Badge variant="outline" className="inline-flex items-center gap-1 border-amber-300 px-1.5 py-0 text-xs text-amber-600">
                            <RefreshCw className="h-3 w-3 animate-spin" />
                            {t('recoverRequests.preSignedUrlsInProgressShort', 'In progress')}
                        </Badge>
                    ) : waiting ? (
                        <Badge variant="outline" className="inline-flex items-center gap-1 border-amber-300 px-1.5 py-0 text-xs text-amber-600">
                            <Hourglass className="h-3 w-3" />
                            {t('recoverRequests.preSignedUrlsWaitingShort', 'Copying to egress')}
                        </Badge>
                    ) : totalFiles === 0 ? (
                        <Badge variant="outline" className="px-1.5 py-0 text-xs text-muted-foreground">
                            {t('recoverRequests.preSignedUrlsNoneShort', 'None')}
                        </Badge>
                    ) : (
                        <Badge variant="secondary" className="px-1.5 py-0 text-xs">
                            {totalFiles}
                        </Badge>
                    )}
                    {renderExpiry(cardExpiry)}
                </button>
                <div className="flex flex-shrink-0 items-center gap-1">
                    {totalFiles > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={handleDownloadAll}
                            title="Download all files"
                        >
                            <FolderDown className="h-3.5 w-3.5" />
                        </Button>
                    )}
                    {canRequest && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={handleRefresh}
                            disabled={isRefreshing}
                            title={t('recoverRequests.refreshPreSignedUrls')}
                        >
                            <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </Button>
                    )}
                </div>
            </div>

            {expanded && withState.map(renderSection)}
        </div>
    )
}
